import * as React from 'react';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { useGetImageAttributes } from '@components/CdnImage/useImageAttributes';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useUser } from '@queries/useUser';
import { useTabSwitcher } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';

import { TabIds } from '../../model';

export const useInjectBazaarvoice = (
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId']
) => {
  const { bazaarvoiceIntegrationPd, bazaarvoiceCookieConsentMapping } =
    useFeatureFlags();
  const { user } = useUser();
  const { setActiveTab, toggleIsReviewTabVisibleOnMobile } = useTabSwitcher();
  const getImageAttributes = useGetImageAttributes();

  React.useEffect(() => {
    if (!bazaarvoiceIntegrationPd) {
      return;
    }

    const { kind, category } = product.primaryCategories;

    window.bvDCC = {
      catalogData: {
        locale: bazaarvoiceIntegrationPd.locale,
        catalogProducts: product.variants.map((variant) => ({
          productId: variant.productCode,
          productName: variant.name,
          productImageURL: getImageAttributes(
            variant.media.mainImage.src,
            'original',
            variant.media.mainImage.alt
          ).src,
          productPageURL: `${window.location.origin}${variant.url}`,
          eans: [variant.eanCode],
          categoryPath: [
            kind && { Name: kind.name, Id: kind.id },
            category && { Name: category.name, Id: category.id },
          ].filter(Boolean),
          brandId: product.brand?.id ?? '',
          brandName: product.brand?.name ?? '',
          family: product.masterProductCode,
        })),
      },
    };

    window.bvCallback = function (BV) {
      for (
        let i = 0, len = window.bvDCC.catalogData.catalogProducts.length;
        i < len;
        i++
      ) {
        BV.pixel.trackEvent('CatalogUpdate', {
          type: 'Product',
          locale: window.bvDCC.catalogData.locale,
          catalogProducts: [window.bvDCC.catalogData.catalogProducts[i]],
        });
      }

      BV.reviews.on('show', () => {
        setActiveTab(TabIds.Reviews);
        toggleIsReviewTabVisibleOnMobile(true);

        setTimeout(() =>
          document.getElementById('pdReviewsScroll').scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          })
        );
      });

      const hasPerformanceCookiesConsent = bazaarvoiceCookieConsentMapping
        ? user.consent.includes('performance')
        : true;
      const hasAnalyticsCookieConsent = bazaarvoiceCookieConsentMapping
        ? user.consent.includes('analytics')
        : true;

      // NOTE: https://knowledge.bazaarvoice.com/wp-content/knowledge/en_US/cookie-consent.html#optional-cookies
      BV.cookieConsent.setConsent({
        bv_segment: hasPerformanceCookiesConsent,
        bv_metrics: hasPerformanceCookiesConsent,
        BVBRANDID: hasPerformanceCookiesConsent,
        BVBRANDSID: hasPerformanceCookiesConsent,
        BVID: hasAnalyticsCookieConsent,
        BVSD: hasAnalyticsCookieConsent,
      });
    };
  }, [
    bazaarvoiceIntegrationPd,
    bazaarvoiceCookieConsentMapping,
    getImageAttributes,
    product.brand?.id,
    product.brand?.name,
    product.variants,
    product.masterProductCode,
    product.primaryCategories,
    setActiveTab,
    toggleIsReviewTabVisibleOnMobile,
    user,
  ]);
};
